/** Renders the toast queue (lib/toast.ts) in polite/assertive live regions. */
import clsx from 'clsx';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { dismissToast, useToasts, type ToastItem } from '../lib/toast.js';
import styles from './ui.module.css';

const ICONS = { info: Info, success: CircleCheck, warning: TriangleAlert, error: CircleAlert } as const;

function ToastView({ t }: { t: ToastItem }) {
  const Icon = ICONS[t.kind];
  return (
    <div className={clsx(styles.toast, styles[`toast_${t.kind}`])}>
      <Icon className={styles.toastIcon} aria-hidden="true" />
      <span className={styles.toastMessage}>{t.message}</span>
      {t.action && (
        <button
          type="button"
          className={clsx('btn btn-sm', styles.toastAction)}
          onClick={() => {
            t.action!.onClick();
            dismissToast(t.id);
          }}
        >
          {t.action.label}
        </button>
      )}
      <button type="button" className={styles.toastClose} onClick={() => dismissToast(t.id)} aria-label="Melding sluiten">
        <X aria-hidden="true" />
      </button>
    </div>
  );
}

export function Toaster() {
  const items = useToasts();
  const errors = items.filter((t) => t.kind === 'error');
  const others = items.filter((t) => t.kind !== 'error');
  return (
    <div className={styles.toaster}>
      <div role="status" aria-live="polite" className={styles.toastRegion}>
        {others.map((t) => (
          <ToastView key={t.id} t={t} />
        ))}
      </div>
      <div role="alert" aria-live="assertive" className={styles.toastRegion}>
        {errors.map((t) => (
          <ToastView key={t.id} t={t} />
        ))}
      </div>
    </div>
  );
}
